// Task permissions and temporal access control
// CREATED: Module for validating task editability based on date and user role
// UPDATED: All profiles (including admins) can only edit tasks for current date
// Implements client-side validation for temporal access rules:
// - ALL users (operators, managers, directors, admins) can only edit tasks for current date
// - Consultation of tasks from other dates remains available for all profiles
// CHANGE: UserRole now imported from supabase.ts (single source of truth)
// CHANGE: Removed all debug console statements for production

import type { AssignedTaskWithDetails } from './execution';
import type { UserRole } from './supabase';
import { formatDate } from './dateUtils';

/**
 * Task temporality classification
 */
export type TaskTemporality = 'past' | 'today' | 'future';

/**
 * Result of editability check
 */
export interface TaskEditability {
  isEditable: boolean;
  reason?: string;
  temporality: TaskTemporality;
}

/**
 * Get current date in YYYY-MM-DD format (client timezone)
 * Note: This should match server's CURRENT_DATE for consistency
 */
export function getCurrentDate(): string {
  return formatDate(new Date());
}

/**
 * Determine the temporality of a task relative to current date
 */
export function getTaskTemporality(taskDate: string): TaskTemporality {
  const today = getCurrentDate();

  if (taskDate < today) {
    return 'past';
  } else if (taskDate > today) {
    return 'future';
  } else {
    return 'today';
  }
}

/**
 * Check if a task is editable based on its date and user role
 *
 * Rules:
 * - ALL users (admins, operators, managers, directors) can only edit tasks for current date
 * - Directors can edit their own assigned tasks (current date only), otherwise read-only
 * - Past tasks: not editable (message: "Cette mission ne peut plus être modifiée")
 * - Future tasks: not editable (message: "Cette mission ne peut pas encore être modifiée")
 *
 * @param task - The task to check
 * @param userRole - The role of the current user
 * @param userId - The ID of the current user (optional, required for director role)
 * @returns EditabilityResult with isEditable flag and reason if not editable
 */
export function isTaskEditable(
  task: AssignedTaskWithDetails,
  userRole: UserRole,
  userId?: string
): TaskEditability {
  // Determine the date to check (use occurrenceDate for virtual occurrences)
  const taskDate = task.isVirtualOccurrence && task.occurrenceDate
    ? task.occurrenceDate
    : task.assigned_date;

  const temporality = getTaskTemporality(taskDate);

  // Directors can edit their own assigned tasks (current date only)
  if (userRole === 'director') {
    // Check if this task is assigned to the director
    const isAssignedToDirector = userId && task.assigned_to_user_id === userId;

    if (isAssignedToDirector) {
      // Apply same rules as operators/managers for their own tasks
      if (temporality === 'today') {
        return {
          isEditable: true,
          temporality
        };
      } else if (temporality === 'past') {
        return {
          isEditable: false,
          reason: 'Cette mission ne peut plus être modifiée',
          temporality
        };
      } else {
        return {
          isEditable: false,
          reason: 'Cette mission ne peut pas encore être modifiée',
          temporality
        };
      }
    } else {
      // For other tasks, directors have read-only access
      return {
        isEditable: false,
        reason: 'Les directeurs ont un accès en lecture seule',
        temporality
      };
    }
  }

  // Admins, Operators and Managers can only edit tasks for current date
  if (userRole === 'admin' || userRole === 'operator' || userRole === 'manager') {
    if (temporality === 'today') {
      return {
        isEditable: true,
        temporality
      };
    } else if (temporality === 'past') {
      return {
        isEditable: false,
        reason: 'Cette liste ne peut plus être modifiée',
        temporality
      };
    } else {
      return {
        isEditable: false,
        reason: 'Cette liste ne peut pas encore être modifiée',
        temporality
      };
    }
  }

  // Default: not editable
  return {
    isEditable: false,
    reason: 'Accès non autorisé',
    temporality
  };
}

/**
 * Get a human-readable message explaining why a task is not editable
 */
export function getTaskEditabilityMessage(
  task: AssignedTaskWithDetails,
  userRole: UserRole,
  userId?: string
): string {
  const editability = isTaskEditable(task, userRole, userId);

  if (editability.isEditable) {
    return 'Cette mission peut être modifiée';
  }

  return editability.reason || 'Cette mission ne peut pas être modifiée';
}

/**
 * Get badge text for task temporality
 */
export function getTemporalityBadgeText(temporality: TaskTemporality): string {
  switch (temporality) {
    case 'past':
      return 'Passée';
    case 'today':
      return "Aujourd'hui";
    case 'future':
      return 'Future';
  }
}

/**
 * Get color classes for task temporality badge
 */
export function getTemporalityBadgeColor(temporality: TaskTemporality): string {
  switch (temporality) {
    case 'past':
      return 'bg-gray-100 text-gray-600 border-gray-200';
    case 'today':
      return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'future':
      return 'bg-blue-100 text-blue-700 border-blue-200';
  }
}

/**
 * Check if user can view a task (read access)
 * Note: This is less restrictive than edit access
 */
export function canViewTask(
  task: AssignedTaskWithDetails,
  userId: string,
  userRole: UserRole
): boolean {
  // Admins and Directors can view all tasks
  if (userRole === 'admin' || userRole === 'director') {
    return true;
  }

  // Operators can view their own tasks
  if (userRole === 'operator') {
    return task.assigned_to_user_id === userId;
  }

  // Managers can view tasks in their sites
  // Note: This requires additional site membership check in the calling code
  if (userRole === 'manager') {
    return true; // Simplified - actual check happens in RLS policies
  }

  return false;
}

/**
 * Log permission denial for debugging and security auditing
 */
export function logPermissionDenial(
  action: string,
  task: AssignedTaskWithDetails,
  userRole: UserRole,
  userId: string
): void {
}

/**
 * Validate if a date string is today
 */
export function isToday(dateString: string): boolean {
  return dateString === getCurrentDate();
}

/**
 * Validate if a date string is in the past
 */
export function isPastDate(dateString: string): boolean {
  return dateString < getCurrentDate();
}

/**
 * Validate if a date string is in the future
 */
export function isFutureDate(dateString: string): boolean {
  return dateString > getCurrentDate();
}
